import React, { useCallback, useEffect, useState } from 'react';
import { EllipsisHorizontalIcon } from '@heroicons/react/24/outline';
import { useResolvedHome, useSetMyNav } from 'api/use-home-preference';
import type { RbacAction } from '@plerionhq/types-rbac';
import { useLocation } from 'react-router-dom';
import {
  useHasAWSIntegrations,
  // useHasIntegrations,
} from '../../api/integration';
import { usePlerionAISettings } from '../../api/plerion-ai';
import { ReorderSideBar } from './ReorderSideBar';
import { SideNavElement } from './SideNavElement';
import { SideNavOptions } from './SideNavOptions';
import { SideNavWrapper } from './SideNavWrapper';
import { SubItemsSideNav } from './SubItemsSideNav';
import {
  PLERI_NAV_ITEM,
  // QUICK_START_NAV_ITEM,
  WELL_ARCHITECTED_FRAMEWORKS_NAV_ITEM,
  WELL_ARCHITECTED_NAV_ITEM,
} from './config';
import { isCustomChecksEnabled } from '../../constants/featureFlags';
import { useAssetOwnershipEnabled } from 'api/use-feature-flags';
import { isAccessGrantsNewBadgeVisible } from 'api/access-grants-new-badge';
import {
  fetchSideNavMode,
  fetchUserNavConfig,
  storeSideNavMode,
} from './fetchUserNavConfig';
import { useTranslation } from 'react-i18next';
export interface SideNavItem {
  icon: string;
  key: string;
  name: string;
  url?: string;
  entitlement?: string[];
  isNew?: boolean;
  showLabel?: boolean;
  toolTipText?: string;
  subItems?: SideNavItem[];
  permissions?: string[];
  /**
   * RBAC action gate; mirrors the backend endpoint's required action. A list
   * is any-of, matching an endpoint that accepts more than one action. An item
   * carrying `permissions` too passes on either, so a custom role reaches it
   * through the action and a built-in role through the legacy list.
   */
  action?: RbacAction | RbacAction[];
  /**
   * false: disables side nav item on the client side.
   * Useful for client-side feature flags.
   */
  enabled?: boolean;
}

export type SideNavMode = 'collapsed' | 'expanded' | 'auto';

export const SideNav = ({
  reportNavItems,
}: {
  reportNavItems: SideNavItem[] | [];
}) => {
  const { t } = useTranslation('containerInterface');
  const [items, setItems] = useState<SideNavItem[]>([]);
  const [mode, setMode] = useState<SideNavMode>(() => fetchSideNavMode());
  const [openSideBar, setOpenSideBar] = useState(mode === 'expanded');
  const [dragging, setDragging] = useState(false);
  const [editingNav, setEditingNav] = useState(false);
  const isExpanded =
    (openSideBar && mode !== 'collapsed') || mode == 'expanded' || editingNav;
  const [activeSubMenu, setActiveSubMenu] = useState<undefined | string>();
  const [allowHoverSubMenu, setAllowHoverSubMenu] = useState<
    undefined | string
  >();
  const location = useLocation();
  const pathName = location.pathname;
  const { data } = usePlerionAISettings();
  const isPleriEnabled = data?.isAllowedAccess;
  const hasAWSIntegrations = useHasAWSIntegrations();
  const [customChecksEnabled, setCustomChecksEnabled] = useState(() =>
    isCustomChecksEnabled(),
  );
  // MI-212: the nav follows the account, not the browser. Order and hidden
  // entries resolve user -> group -> tenant server-side; any localStorage
  // order left over from before seeds the first server write.
  const { data: resolvedPreferences } = useResolvedHome();
  const serverNav = resolvedPreferences?.navConfig ?? null;
  const navSource = resolvedPreferences?.navSource ?? 'none';
  const { mutate: saveNav } = useSetMyNav();
  const [showHidden, setShowHidden] = useState(false);
  const ownershipEnabled = useAssetOwnershipEnabled();
  const hiddenKeys = React.useMemo(() => serverNav?.hidden ?? [], [serverNav]);

  useEffect(() => {
    const onStorage = () => {
      setCustomChecksEnabled(isCustomChecksEnabled());
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  useEffect(() => {
    const mergedItems = fetchUserNavConfig(
      [...(hasAWSIntegrations ? [WELL_ARCHITECTED_NAV_ITEM] : [])],
      [...(hasAWSIntegrations ? [WELL_ARCHITECTED_FRAMEWORKS_NAV_ITEM] : [])],
    );

    if (reportNavItems?.length > 0) {
      mergedItems.push({
        key: 'pinned-custom-reports',
        name: 'Custom reports',
        icon: 'CustomReportIcon',
        subItems: reportNavItems,
        url: '/settings/custom-reports',
      });
    }

    const settingsItem = mergedItems.find((item) => item.key === 'settings');

    if (settingsItem?.subItems) {
      const customReportsIndex = settingsItem.subItems.findIndex(
        (item) => item.key === 'custom-report-builder',
      );
      const insertAt =
        customReportsIndex >= 0
          ? customReportsIndex + 1
          : settingsItem.subItems.length;
      settingsItem.subItems.splice(insertAt, 0, {
        name: 'Custom checks',
        url: '/settings/custom-checks',
        icon: 'BeakerIcon',
        key: 'custom-checks',
        // The management page: its detail and write routes are on the check
        // action alone. The list route also admits Finding:Read, but its own
        // declaration says why -- "custom checks produce findings, so the list
        // is part of the vocabulary a finding reader renders against" -- which
        // is not a reason to offer a finding reader this page.
        action: 'CustomCheck:Read',
      });
    }

    // Sits with Asset groups: both group things, and both are scoped to the
    // tenant you are in. Configuring it is organization-admin-only, matching
    // the tag-key endpoints.
    if (settingsItem?.subItems) {
      const assetGroupsIndex = settingsItem.subItems.findIndex(
        (item) => item.key === 'asset-group',
      );
      settingsItem.subItems.splice(
        assetGroupsIndex >= 0
          ? assetGroupsIndex + 1
          : settingsItem.subItems.length,
        0,
        {
          name: 'Integration groups',
          url: '/settings/integration-groups',
          icon: 'RectangleGroupIcon',
          key: 'integration-groups',
          permissions: ['organization:Administration'],
          // Selecting the tag keys that form the groups is
          // PUT /api/core/tenant/integration-groups/config.
          action: 'Organization:Modify',
        },
      );
    }

    // Asset ownership sits with the other grouping settings. The page reads
    // on Asset:Read and gates its changes on Owner:Modify and
    // OwnershipRule:Modify.
    if (settingsItem?.subItems && ownershipEnabled) {
      const integrationGroupsIndex = settingsItem.subItems.findIndex(
        (item) => item.key === 'integration-groups',
      );
      settingsItem.subItems.splice(
        integrationGroupsIndex >= 0
          ? integrationGroupsIndex + 1
          : settingsItem.subItems.length,
        0,
        {
          name: 'Asset ownership',
          url: '/settings/asset-ownership',
          icon: 'UserGroupIcon',
          key: 'ownership',
          action: 'Asset:Read',
        },
      );
    }

    if (isPleriEnabled) {
      settingsItem?.subItems?.push(PLERI_NAV_ITEM);
    }

    // Highlight Entitlements while access grants is freshly moved into it.
    // Expires on its own — see ACCESS_GRANTS_NEW_BADGE_UNTIL.
    if (isAccessGrantsNewBadgeVisible()) {
      const entitlementsItem = mergedItems.find((item) => item.key === 'ciem');
      if (entitlementsItem) {
        entitlementsItem.isNew = true;
        entitlementsItem.toolTipText = t(
          'resourceAccessGrantsNowShowAssetPermissions',
        );
      }
    }

    if (serverNav?.order?.length) {
      const ordered = serverNav.order
        .map((key) => mergedItems.find((item) => item.key === key))
        .filter(Boolean) as SideNavItem[];
      const rest = mergedItems.filter(
        (item) => !serverNav.order.includes(item.key),
      );
      setItems([...ordered, ...rest]);
      return;
    }
    setItems(mergedItems);
  }, [
    hasAWSIntegrations,
    reportNavItems,
    isPleriEnabled,
    serverNav,
    ownershipEnabled,
    t,
  ]);

  // framer-motion calls onReorder continuously during a drag, so this only
  // moves local state; the server write happens once, when the drag ends.
  const updateNavItems = useCallback(
    (reordered: SideNavItem[]) => {
      setItems((previous) => {
        const hiddenItems = previous.filter((item) =>
          hiddenKeys.includes(item.key),
        );
        return [...reordered, ...hiddenItems];
      });
    },
    [hiddenKeys],
  );

  const itemsRef = React.useRef<SideNavItem[]>([]);
  useEffect(() => {
    itemsRef.current = items;
  }, [items]);

  const onDraggingChange = useCallback(
    (value: boolean) => {
      setDragging(value);
      if (!value) {
        saveNav({
          order: itemsRef.current.map((item) => item.key),
          hidden: hiddenKeys,
        });
      }
    },
    [hiddenKeys, saveNav],
  );

  const toggleHidden = useCallback(
    (key: string) => {
      const hidden = hiddenKeys.includes(key)
        ? hiddenKeys.filter((candidate) => candidate !== key)
        : [...hiddenKeys, key];
      saveNav({ order: items.map((item) => item.key), hidden });
    },
    [hiddenKeys, items, saveNav],
  );

  const visibleItems = items.filter((item) => !hiddenKeys.includes(item.key));
  const hiddenItems = items.filter((item) => hiddenKeys.includes(item.key));

  useEffect(() => {
    const activeComp = [...items].find((x) => {
      return (
        x.url === pathName ||
        (pathName.includes('/settings/custom-reports') &&
          location.search.includes('?mode=readOnly') &&
          x.key === 'pinned-custom-reports') ||
        (pathName.includes(x.url ?? '') &&
          !location.search.includes('?mode=readOnly')) ||
        x.subItems?.some((s) => s.url === pathName)
      );
    });

    if (activeComp?.subItems) {
      setAllowHoverSubMenu(activeComp?.key);
    } else {
      setAllowHoverSubMenu(undefined);
      setActiveSubMenu(undefined);
    }
  }, [pathName, reportNavItems, items, location.search]);

  return (
    <React.Fragment>
      <SideNavWrapper
        mode={mode}
        setOpenSideBar={setOpenSideBar}
        isExpanded={isExpanded}
        onMouseOver={() => {
          if (mode === 'auto') {
            setOpenSideBar(true);
          }
          if (allowHoverSubMenu) {
            setActiveSubMenu(allowHoverSubMenu);
          }
        }}
        onMouseOut={() => {
          if (allowHoverSubMenu) {
            setActiveSubMenu(undefined);
          }
          if (mode === 'auto' && !dragging && !editingNav) {
            setOpenSideBar(false);
          }
        }}
      >
        <div className="z-[99] flex h-full w-full flex-col gap-y-2 border-r border-gray-300 bg-white p-2 pt-4 dark:border-gray-700 dark:bg-gray-900">
          {editingNav && isExpanded && (
            <div className="border-br-primary bg-br-primary/5 -mx-2 -mt-4 mb-1 flex items-center justify-between border-t-2 px-3 py-2">
              <span className="text-xs text-gray-600 dark:text-gray-300">
                {navSource === 'user' && t('editingCustom')}
                {navSource === 'group' && t('editingGroupDefault')}
                {navSource === 'tenant' && t('editingTenantDefault')}
                {navSource === 'none' && t('editing')}
              </span>
              <span className="flex items-center gap-2">
                {navSource === 'user' && (
                  <button
                    type="button"
                    className="cursor-pointer text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                    onClick={() => saveNav(null)}
                  >
                    {t('reset')}
                  </button>
                )}
                {/* The design-system button is a toolbar-scale pill that
                    overwhelms a 140px rail; a compact accent text button
                    carries the affordance at this size. */}
                <button
                  type="button"
                  className="cursor-pointer rounded px-1.5 py-0.5 text-xs font-semibold text-orange-600 hover:bg-orange-500/10 dark:text-orange-400"
                  onClick={() => {
                    setEditingNav(false);
                    if (mode === 'auto') {
                      setOpenSideBar(false);
                    }
                  }}
                  data-testid="nav-edit-done"
                >
                  {t('done')}
                </button>
              </span>
            </div>
          )}
          <ReorderSideBar
            items={visibleItems}
            updateNavItems={updateNavItems}
            mode={mode}
            setDragging={onDraggingChange}
            isExpanded={isExpanded}
            setActiveSubMenu={setActiveSubMenu}
            setAllowHoverSubMenu={setAllowHoverSubMenu}
            activeSubMenu={activeSubMenu}
            editMode={editingNav && isExpanded}
            onToggleHidden={toggleHidden}
          />
          {hiddenItems.length > 0 && (
            <div className="border-t border-gray-200 pt-1 dark:border-gray-700">
              <button
                type="button"
                className="flex w-full cursor-pointer items-center gap-2 rounded-md px-2 py-1 text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800"
                title={t('hiddenPages')}
                onClick={() => setShowHidden((open) => !open)}
              >
                <EllipsisHorizontalIcon className="h-5 w-5 flex-shrink-0" />
                {isExpanded && (
                  <span className="text-xs">
                    {t('hidden', { length: hiddenItems.length })}
                  </span>
                )}
              </button>
              {(showHidden || editingNav) && isExpanded && (
                <div className="flex flex-col gap-y-1 opacity-70">
                  {hiddenItems.map((item) => (
                    <div key={item.key} className="relative">
                      <SideNavElement
                        isExpanded={isExpanded}
                        item={item}
                        mode={mode}
                        setActiveSubMenu={setActiveSubMenu}
                        setAllowHoverSubMenu={setAllowHoverSubMenu}
                        activeSubMenu={activeSubMenu}
                      />
                      {editingNav && (
                        <button
                          type="button"
                          title={t('showInNavigation')}
                          className="absolute top-1/2 right-0 flex h-8 w-8 -translate-y-1/2 cursor-pointer items-center justify-center rounded text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                          onClick={() => toggleHidden(item.key)}
                        >
                          <ShowEyeIcon />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          <SideNavOptions
            isExpanded={isExpanded}
            mode={mode}
            setMode={(mode) => {
              setMode(mode);
              storeSideNavMode(mode);
              if (mode === 'auto') {
                setOpenSideBar(false);
              }
            }}
            onEditNav={() => {
              // Editing holds the sidebar open for the duration; the person's
              // chosen mode is left exactly as it was.
              setEditingNav(true);
              setOpenSideBar(true);
            }}
          />
        </div>
        <SubItemsSideNav
          {...{
            items,
            activeSubMenu,
            setActiveSubMenu,
            setOpenSideBar,
            allowHoverSubMenu,
            setAllowHoverSubMenu,
          }}
        />
      </SideNavWrapper>
    </React.Fragment>
  );
};

const ShowEyeIcon = () => (
  <svg
    className="h-4 w-4"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M3.98 8.223A10.477 10.477 0 0 0 1.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.451 10.451 0 0 1 12 4.5c4.756 0 8.773 3.162 10.065 7.498a10.522 10.522 0 0 1-4.293 5.774M6.228 6.228 3 3m3.228 3.228 3.65 3.65m7.894 7.894L21 21m-3.228-3.228-3.65-3.65m0 0a3 3 0 1 0-4.243-4.243m4.242 4.242L9.88 9.88"
    />
  </svg>
);
